import React, { useCallback, useEffect, useRef, useState } from 'react';

import Pagination from './Pagination';
import Spinner from './Spinner';
import TableService from '../services/table.service';
import useAutoRefresh from '../common/http/useAutoRefresh';
import { formatCurrency } from '../common/utils';
import { statusCls } from '../utils/order';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const errMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message;

const STATUSES = ['pending', 'preparing', 'served', 'paid', 'cancelled'];

// Staff page (/manage-table-orders): view/manage orders placed from tables.
// Separate from normal customer orders.
const TableOrderManagement = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ items: [], totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState({ status: '', q: '' });
  const [page, setPage] = useState(1);
  // `sort` picks the field; when `groupByTable` is on, the server sorts by table
  // first so orders of the same table stay together. Preference is remembered.
  const [sort, setSort] = useState({ field: 'date', dir: 'desc' });
  const [groupByTable, setGroupByTable] = useState(() => localStorage.getItem('tableOrders.groupByTable') !== '0');

  // Hide cancelled orders (remembered). An explicit "Cancelled" status filter
  // still shows them.
  const [hideCancelled, setHideCancelled] = useState(() => localStorage.getItem('tableOrders.hideCancelled') === '1');

  const toggleHideCancelled = (checked) => {
    setHideCancelled(checked);
    setPage(1);
    localStorage.setItem('tableOrders.hideCancelled', checked ? '1' : '0');
  };

  // Hide preparing / served / paid orders (remembered). An explicit status filter still shows them.
  const [hidePreparing, setHidePreparing] = useState(() => localStorage.getItem('tableOrders.hidePreparing') === '1');
  const [hideServed, setHideServed] = useState(() => localStorage.getItem('tableOrders.hideServed') === '1');
  const [hidePaid, setHidePaid] = useState(() => localStorage.getItem('tableOrders.hidePaid') === '1');
  const toggleHide = (key, setter) => (checked) => {
    setter(checked);
    setPage(1);
    localStorage.setItem(`tableOrders.${key}`, checked ? '1' : '0');
  };

  // Optional columns (remembered): customer name and created-by.
  const [showCustomer, setShowCustomer] = useState(() => localStorage.getItem('tableOrders.showCustomer') !== '0');
  const [showCreatedBy, setShowCreatedBy] = useState(() => localStorage.getItem('tableOrders.showCreatedBy') !== '0');
  const toggleColumn = (key, setter) => (checked) => {
    setter(checked);
    localStorage.setItem(`tableOrders.${key}`, checked ? '1' : '0');
  };

  const toggleGroup = (checked) => {
    setGroupByTable(checked);
    setPage(1);
    localStorage.setItem('tableOrders.groupByTable', checked ? '1' : '0');
  };

  // Only the latest request may update the table (see OrderManagement).
  const reqSeq = useRef(0);
  // Manual "Refresh" button: reloads in place (spinner in the button only).
  const [refreshing, setRefreshing] = useState(false);

  // silent: background auto-refresh — no spinner, no error toast.
  // onDone: called after the request settles (manual refresh).
  const load = useCallback((silent = false, onDone) => {
    const seq = ++reqSeq.current;
    if (!silent) setLoading(true);
    const params = { page, size: 10, sort: sort.field, dir: sort.dir, group: groupByTable ? 1 : 0 };
    if (applied.status) params.status = applied.status;
    if (applied.q) params.q = applied.q;
    if (hideCancelled) params.hide_cancelled = 1;
    if (hidePreparing) params.hide_preparing = 1;
    if (hideServed) params.hide_served = 1;
    if (hidePaid) params.hide_paid = 1;
    TableService.getOrders(params, { silent })
      .then((res) => {
        if (seq === reqSeq.current) setData(res.data);
      })
      .catch((err) => {
        if ((!silent || onDone) && seq === reqSeq.current) toast.error(errMsg(err));
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
        if (onDone) onDone();
      });
  }, [page, applied, sort, groupByTable, hideCancelled, hidePreparing, hideServed, hidePaid]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto refresh every 5s (skipped while a load is running).
  useAutoRefresh(() => {
    if (!loading && !refreshing) load(true);
  }, 5000);

  const refresh = () => {
    setRefreshing(true);
    load(true, () => setRefreshing(false));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    setApplied({ status: statusFilter, q: search.trim() });
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
    setPage(1);
    setApplied({ status: '', q: '' });
  };

  const toggleSort = (field) => {
    setPage(1);
    setSort((s) => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: field === 'name' ? 'asc' : 'desc' }));
  };

  const SortTh = ({ field, label, style }) => {
    const active = sort.field === field;
    return (
      <th style={{ cursor: 'pointer', whiteSpace: 'nowrap', ...style }} className='user-select-none' onClick={() => toggleSort(field)} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} title={t('tableOrders.sortBy', { field: label })}>
        {label} <span className={active ? '' : 'text-muted opacity-50'}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </th>
    );
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? String(d) : dt.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  };

  const changeStatus = (order, status) => {
    if (status === order.status) return;
    TableService.updateOrderStatus(order.id, status)
      .then((res) => {
        // Drop it from view when its new status is currently hidden.
        const hiddenNow = (status === 'cancelled' && hideCancelled) || (status === 'preparing' && hidePreparing) || (status === 'served' && hideServed) || (status === 'paid' && hidePaid);
        const hideNow = hiddenNow && applied.status !== status;
        setData((d) => ({
          ...d,
          items: hideNow ? d.items.filter((o) => o.id !== order.id) : d.items.map((o) => (o.id === order.id ? res.data : o)),
        }));
        toast.success(t('tableOrders.statusChanged', { id: order.id, status: t(`tableOrderStatus.${status}`, status) }));
      })
      .catch((err) => toast.error(errMsg(err)));
  };

  return (
    <div className='container mt-4'>
      <h3 className='mb-1'>{t('tableOrders.title')}</h3>
      <p className='text-muted'>{t('tableOrders.subtitle')}</p>

      <form className='row g-2 align-items-end mb-3' onSubmit={applyFilters}>
        <div className='col-md-3'>
          <select className='form-select' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value=''>{t('tableOrders.allStatuses')}</option>
            {STATUSES.map((s) => (
              <option value={s} key={s}>
                {t(`tableOrderStatus.${s}`, s)}
              </option>
            ))}
          </select>
        </div>
        <div className='col-md-4'>
          <input type='text' className='form-control' placeholder={t('tableOrders.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className='col-auto'>
          <button className='btn btn-primary' type='submit'>
            {t('common.search')}
          </button>
        </div>
        {(applied.status || applied.q) && (
          <div className='col-auto'>
            <button className='btn btn-outline-secondary' type='button' onClick={clearFilters}>
              {t('common.clear')}
            </button>
          </div>
        )}
        <div className='col-auto ms-auto'>
          <button type='button' className='btn btn-outline-secondary' onClick={refresh} disabled={loading || refreshing} title={t('common.refresh')}>
              {refreshing ? <span className='spinner-border spinner-border-sm me-1' aria-hidden='true' /> : <span className='me-1' aria-hidden='true'>↻</span>}
              {t('common.refresh')}
            </button>
        </div>
      </form>

      <div className='d-flex flex-wrap align-items-center gap-3 mb-2'>
        <span className='text-muted small'>{t('tableOrders.columns')}:</span>
        <div className='form-check form-check-inline mb-0'>
          <input className='form-check-input' type='checkbox' id='col-customer' checked={showCustomer} onChange={(e) => toggleColumn('showCustomer', setShowCustomer)(e.target.checked)} />
          <label className='form-check-label' htmlFor='col-customer'>
            {t('tableOrders.customer')}
          </label>
        </div>
        <div className='form-check form-check-inline mb-0'>
          <input className='form-check-input' type='checkbox' id='col-created-by' checked={showCreatedBy} onChange={(e) => toggleColumn('showCreatedBy', setShowCreatedBy)(e.target.checked)} />
          <label className='form-check-label' htmlFor='col-created-by'>
            {t('tableOrders.createdBy')}
          </label>
        </div>
      </div>

      <div className='d-flex flex-wrap gap-3'>
        <div className='form-check mb-2'>
          <input className='form-check-input' type='checkbox' id='hide-cancelled' checked={hideCancelled} onChange={(e) => toggleHideCancelled(e.target.checked)} />
          <label className='form-check-label' htmlFor='hide-cancelled'>
            {t('tableOrders.hideCancelled')}
          </label>
        </div>

        <div className='form-check mb-2'>
          <input className='form-check-input' type='checkbox' id='hide-preparing' checked={hidePreparing} onChange={(e) => toggleHide('hidePreparing', setHidePreparing)(e.target.checked)} />
          <label className='form-check-label' htmlFor='hide-preparing'>
            {t('tableOrders.hidePreparing')}
          </label>
        </div>

        <div className='form-check mb-2'>
          <input className='form-check-input' type='checkbox' id='hide-served' checked={hideServed} onChange={(e) => toggleHide('hideServed', setHideServed)(e.target.checked)} />
          <label className='form-check-label' htmlFor='hide-served'>
            {t('tableOrders.hideServed')}
          </label>
        </div>

        <div className='form-check mb-2'>
          <input className='form-check-input' type='checkbox' id='hide-paid' checked={hidePaid} onChange={(e) => toggleHide('hidePaid', setHidePaid)(e.target.checked)} />
          <label className='form-check-label' htmlFor='hide-paid'>
            {t('tableOrders.hidePaid')}
          </label>
        </div>

        <div className='form-check mb-2'>
          <input className='form-check-input' type='checkbox' id='group-by-table' checked={groupByTable} onChange={(e) => toggleGroup(e.target.checked)} />
          <label className='form-check-label' htmlFor='group-by-table'>
            {t('tableOrders.groupByTable')}
          </label>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : data.items.length === 0 ? (
        <p className='text-muted'>{t('tableOrders.empty')}</p>
      ) : (
        <div className='table-responsive rounded mt-4'>
          <table className='table table-hover align-middle'>
            <thead>
              <tr>
                <th style={{ width: 60 }}>{t('common.id')}</th>
                <SortTh field='name' label={t('tableOrders.table')} />
                <th>{t('tableOrders.items')}</th>
                {showCustomer && <th>{t('tableOrders.customer')}</th>}
                {showCreatedBy && <th>{t('tableOrders.createdBy')}</th>}
                <SortTh field='total' label={t('tableOrders.total')} style={{ width: 120 }} />
                <SortTh field='date' label={t('tableOrders.created')} style={{ width: 170 }} />
                <SortTh field='status' label={t('tableOrders.status')} style={{ width: 180 }} />
              </tr>
            </thead>
            <tbody>
              {data.items.map((o, i) => (
                <tr key={o.id} style={groupByTable && i > 0 && data.items[i - 1].table_name !== o.table_name ? { borderTop: '2px solid var(--bs-gray-500)' } : undefined}>
                  <td>#{o.id}</td>
                  <td>
                    <span className='badge bg-dark'>{o.table_name}</span>
                  </td>
                  <td className='small'>
                    {o.items.map((it) => (
                      <div key={it.id}>
                        {it.product_name} × {it.quantity}
                      </div>
                    ))}
                    {o.note && <div className='text-muted fst-italic'>“{o.note}”</div>}
                  </td>
                  {showCustomer && <td className='small'>{o.customer_name || '—'}</td>}
                  {showCreatedBy && <td className='small'>{o.created_by_name || <span className='text-muted'>{t('tableOrders.guest')}</span>}</td>}
                  <td>{formatCurrency(o.total_amount)}</td>
                  <td className='small text-nowrap'>{fmtDate(o.createdAt)}</td>
                  <td>
                    <span className={`badge ${statusCls(o.status)} me-2`}>{t(`tableOrderStatus.${o.status}`, o.status)}</span>
                    <select className='form-select form-select-sm mt-1' value={o.status} onChange={(e) => changeStatus(o, e.target.value)}>
                      {STATUSES.map((s) => (
                        <option value={s} key={s}>
                          {t(`tableOrderStatus.${s}`, s)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={data.currentPage} totalPages={data.totalPages} onChange={setPage} />
    </div>
  );
};

export default TableOrderManagement;
